// SurfaceMaterialManager.ts — Buildings Set1 §7.2: the "give me a material for an arbitrary mesh"
// renderer layer, deliberately much smaller than `TerrainMaterialManager`. It assembles a
// single-recipe material in SURFACE mode (§7.1 — `assembleMaterialShader(..., {surfaceMode:true})`),
// caches the template `ShaderMaterial` keyed by the material-set hash, and hands the renderer a
// per-mesh clone bound to the same shared param UBO — exactly the terrain manager's
// template+clone+shared-UBO pattern, minus the per-chunk lattice/splat/region plumbing.
//
// The living detail: an ARBITRARY mesh surface (a Building's walls/floors/roof + Door/Window
// models) is meshed in SURFACE-UV space (metres along the surface — the mesh's `uv` attribute),
// with the lighting normal taken from the GEOMETRY normal rather than a per-chunk lattice.
// `mesh.uv` carries the surface UV; the vertex shader forwards it as `vSurfaceUV` (metres) and
// the interpolated geometry normal as `vSurfaceNormal`.
import * as THREE from "three";
import {assembleMaterialShader, type AssembleResult} from "../../Terrain/Materials/Compose/ShaderAssembler.js";
import {packMaterialParams, type PackedParamLayout} from "../../Terrain/Materials/Compose/UniformPacker.js";
import type {ResolvedRecipe} from "../../Terrain/Materials/RecipeResolverCore.js";
import type {ParamValue} from "../../Terrain/Materials/@MaterialTypes.js";
import {GetAsync} from "mobx-graphlink";

// NOTE: `ResolveRecipe` (the per-map DB accessor) is imported LAZILY — this module is imported
// by `Building3D.tsx` (a UI file that already pulls in the whole apollo/mobx-graphlink stack), so
// the static import would be fine there. But keeping it lazy mirrors `TerrainMaterialManager`'s
// discipline (a Node test could import this for the template-building path without dragging the
// DB accessors in). The surface template builder (`buildSurfaceMaterialTemplate`) is DB-free.

/** The surface-mode vertex shader (GLSL3 — three prepends `#version 300 es` + prefix). It
 *  forwards the mesh's UV (surface metres) as `vSurfaceUV` and the interpolated GEOMETRY normal
 *  as `vSurfaceNormal` (the surface-mode lighting base normal), and computes the world position
 *  for the real view-v vector in specular (vWorldPos, D10). */
export const SURFACE_VERTEX_GLSL = `// Building surface vertex shader (§7.2). The mesh's UV attribute is the SURFACE UV in metres
// (walls: (along-wall, Z); floors/roof: (x, y) metres) — so vSurfaceUV IS the p the material
// samples, and the primitives' scales (metres) are directly meaningful. normal is the
// geometry normal (the lighting base normal). uv/normal are the standard three attributes.
out vec2 vSurfaceUV;
out vec3 vSurfaceNormal;
out vec3 vWorldPos;
void main() {
	vSurfaceUV = uv;
	vSurfaceNormal = normal;
	#include <begin_vertex>     // vec3 transformed = vec3(position);
	#include <project_vertex>
	vWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;
}
`;

/** One cached surface-material template: the three.js ShaderMaterial + its shared param UBO. */
export interface SurfaceMaterialEntry {
	material: THREE.ShaderMaterial;
	paramGroup: THREE.UniformsGroup;
	paramLayouts: PackedParamLayout[];
	setHash: string;
	recipe: ResolvedRecipe;
	recipeID: string;
}

/**
 * Renderer-side manager for the Buildings Set1 SURFACE materials (§7.2). Constructed per
 * `MapObject3D`/`Building3D` mount. `getSurfaceMaterial(mapID, recipeID, opts)` resolves the
 * recipe via `ResolveRecipe`, assembles + caches the template `ShaderMaterial`, and returns an
 * entry the renderer clones per mesh (setting `u_surfaceElevation`, `u_metersPerPixel`, and the
 * light rig on the clone). Caches by `materialSetHash` — a params-only recipe variant resolves to
 * the SAME program (the D1 free-variant property shared with terrain).
 */
export class SurfaceMaterialManager {
	/** Program cache: set hash → template entry. */
	private programs = new Map<string, SurfaceMaterialEntry>();
	/** The most recently used entry (the overwhelmingly common single-recipe case stays fast). */
	private program: SurfaceMaterialEntry | null = null;

	/** Resolve + assemble + cache the surface material for a single recipe id.
	 *  @param opts.transparent — when true, the returned material is a transparent pass (glass):
	 *  `transparent: true, depthWrite: false` with the recipe's opacity in `u_opacity` (default 1 —
	 *  the glass recipe sets its own albedo/roughness; see Recipe_Glass). */
	async getSurfaceMaterial(
		mapID: string,
		recipeID: string,
		opts: {transparent?: boolean} = {},
	): Promise<SurfaceMaterialEntry> {
		const {ResolveRecipe} = await import("../../Server/DB/mapObjects/ProcMatAccessors.js");
		const resolved = ResolveRecipe(recipeID, mapID);
		const setHash = resolved.structuralHash;
		const existing = this.programs.get(setHash);
		if (existing) { this.program = existing; return existing; }

		const assembled = assembleMaterialShader([resolved], {
			includeLighting: true,
			surfaceMode: true,
			roughnessAwareSpecular: true,
			outputTransfer: true,
		});
		const paramLayouts = assembled.paramLayouts;
		const paramGroup = this.buildParamGroup(paramLayouts);
		const material = buildSurfaceMaterialTemplate(assembled, paramLayouts, ()=>paramGroup, THREE, opts.transparent === true);

		const entry: SurfaceMaterialEntry = {material, paramGroup, paramLayouts, setHash, recipe: resolved, recipeID};
		this.programs.set(setHash, entry);
		this.program = entry;
		return entry;
	}

	/** The most recently used entry (or null). */
	get lastProgram(): SurfaceMaterialEntry | null { return this.program; }

	/** The three.js renderer the manager pre-warms three's program cache through (attached by the
	 *  renderer; null before then). Buildings Set1 P7 — P7 §"Program pre-warm": used ONLY for the
	 *  three.js-layer warm-up (compiling the building's resolved recipe set into three's
	 *  WebGLProgram cache ahead of the first appearance, so walking up to a building never hitches
	 *  on a first-draw shader compile). The raw WebGL2 context is never needed here — surface
	 *  materials compile through three's own program cache (the same cache the live render path
	 *  reads), so attaching the WebGLRenderer is sufficient. */
	private warmRenderer: THREE.WebGLRenderer | null = null;

	/** P7 — scratch ShaderMaterials holding the pre-warmed three.js programs alive until the live
	 *  template material appears (a three.js program is destroyed when its last referencing
	 *  material is disposed/GC'd — `usedTimes` hits 0 — so the warm-up MUST keep the scratch
	 *  materials referenced across the idle → first-appearance gap).  Keyed by set hash, and the
	 *  transparent-pass scratches are kept separately from the opaque ones because three bakes
	 *  `transparent` into the program cache key (`WebGLPrograms.getParameters`' `opaque`), so a
	 *  glass scratch and its opaque sibling compile DIFFERENT programs. */
	private warmOpaque = new Map<string, THREE.ShaderMaterial>();
	private warmGlass = new Map<string, THREE.ShaderMaterial>();

	/** Attach the three.js renderer (for the P7 three.js-layer program pre-warm).  The renderer is
	 *  created on the SAME gl context; the manager never renders through it for the building —
	 *  it only calls `renderer.compile` on scratch scenes during idle. */
	attachRenderer(renderer: THREE.WebGLRenderer): void {
		if (this.warmRenderer === renderer) return;
		this.warmRenderer = renderer;
		// A new renderer = a fresh three.js program cache — previous warm-ups are meaningless.
		this.retireWarmMaterials();
	}

	/** Dispose + drop every held warm scratch (called on renderer change and on `dispose`). */
	private retireWarmMaterials(): void {
		for (const m of this.warmOpaque.values()) m.dispose();
		for (const m of this.warmGlass.values()) m.dispose();
		this.warmOpaque.clear();
		this.warmGlass.clear();
	}

	/** P7 — program-cache warm-up for the building's resolved recipe set. Compiles the opaque +
	 *  transparent variants of every DISTINCT recipe into three's WebGLProgram cache (via scratch
	 *  ShaderMaterials rendered once offscreen through the attached renderer), so the first live
	 *  appearance of each building material is a program cache HIT (no shader-compile hitch).
	 *
	 *  Best-effort + idempotent: a recipe already in the module program cache (or one with no
	 *  attached renderer) is skipped; a compile error logs and is swallowed (the live
	 *  `getSurfaceMaterial` path surfaces it properly).  Runs OFF the frame loop (the renderer
	 *  schedules it via a macrotask) so it cannot block the visible frame.
	 *
	 *  The scratch must compile the SAME fragment string / uniform decls the live template uses and
	 *  must be KEPT ALIVE until the live template appears (held in `warmOpaque`/`warmGlass`), or
	 *  three destroys the program when the scratch is GC'd.
	 *
	 *  @param recipeIDs The DISTINCT recipe ids the building's opaque+transparent material slots
	 *  resolve to (via the `ResolvedRoomMaterials`/roof material maps). Empty => nothing to warm.
	 *  @returns The set hashes actually compiled (empty when everything was already cached). */
	async warmUpPrograms(recipeIDs: readonly string[]): Promise<string[]> {
		const renderer = this.warmRenderer;
		if (!renderer) return [];
		const {ResolveRecipe} = await import("../../Server/DB/mapObjects/ProcMatAccessors.js");
		const seen = new Set<string>();
		const compiled: string[] = [];

		const resolvedRecipes = await GetAsync(()=>{
			return [...recipeIDs].ToMap(id=>id, id=>ResolveRecipe(id, id));
		});

		for (const id of recipeIDs) {
			if (id == null || id == "") continue;
			// Each recipe variant gets its opaque + transparent pass warmed (a door's wood appears
			// opaque today, but keeping glass always warm is cheap and future-proof).
			for (const transparent of [false, true]) {
				const key = `${id}${transparent ? ":glass" : ""}`;
				if (seen.has(key)) continue;
				seen.add(key);
				try {
					if (this.warmPass(id, transparent, resolvedRecipes.get(id)!)) compiled.push(key);
				} catch (err) {
					// Best-effort: a bad recipe is a data error the live path reports properly.
					console.warn(`[SurfaceMaterialManager] warm-up of recipe "${id}"${transparent ? " (glass)" : ""} failed.`, err);
				}
			}
		}
		return compiled;
	}

	/** Compile a single recipe variant into three's program cache via a scratch ShaderMaterial.
	 *  Returns true when a NEW program was compiled (was NOT already cached), false when skipped
	 *  (already warm, or the recipe failed to assemble). The scratch is held in `warmOpaque`/
	 *  `warmGlass` until `dispose`/renderer change. */
	private warmPass(recipeID: string, transparent: boolean, resolvedRecipe: import("../../Terrain/Materials/RecipeResolverCore.js").ResolvedRecipe): boolean {
		// `ResolveRecipe(recipeID, mapID)` needs a map; pre-warm uses recipe id as the mapID
		// (built-ins resolve independently of the map; the live path overrides with the real map).
		const resolved = resolvedRecipe;
		const setHash = resolved.structuralHash;
		const hold = transparent ? this.warmGlass : this.warmOpaque;
		if (hold.has(setHash)) return false;

		// Assemble identically to `getSurfaceMaterial` (surfaceMode + lit + roughness-aware
		// specular + output transfer) so the scratch's fragment string is byte-identical to the
		// live template's — three's program cache key is content-based.
		const assembled = assembleMaterialShader([resolved], {
			includeLighting: true,
			surfaceMode: true,
			roughnessAwareSpecular: true,
			outputTransfer: true,
		});
		const paramLayouts = assembled.paramLayouts;
		const scratch = buildSurfaceMaterialTemplate(assembled, paramLayouts, ()=>this.buildParamGroup(paramLayouts), THREE, transparent);

		// Compile the scratch through the attached renderer — this is what actually populates
		// three's WebGLProgram cache for the exact cacheKey the live template will hit.
		// `renderer.compile(scene, camera)` only compiles programs (it does NOT draw), so it
		// cannot disturb the visible frame; the scratch scene is throwaway, and only `scratch` is
		// kept alive. The scratch is a Camera-driven Mesh so three picks it up in the compile
		// pass regardless of any framebuffer state.
		const renderer = this.warmRenderer;
		if (!renderer) return false;
		const warmScene = new THREE.Scene();
		warmScene.add(new THREE.Mesh(new THREE.PlaneGeometry(1, 1), scratch));
		renderer.compile(warmScene, this.warmCamera);

		hold.set(setHash, scratch);
		return true;
	}

	/** Scratch ortho camera for the warm-up compile pass (never rendered for real). */
	private warmCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);

	/** Clone the template material for a specific mesh, sharing the UBO. The clone is what the
	 *  renderer sets per-mesh uniforms on (surface elevation, light rig, metres-per-pixel). */
	cloneSurfaceMaterial(entry: SurfaceMaterialEntry): THREE.ShaderMaterial {
		const mat = entry.material.clone() as THREE.ShaderMaterial;
		mat.uniforms = THREE.UniformsUtils.clone(entry.material.uniforms);
		mat.uniformsGroups = entry.material.uniformsGroups;
		return mat;
	}

	/** Build the std140 param UBO for a set of layouts — mirrors `TerrainMaterialManager.buildParamGroup`. */
	buildParamGroup(paramLayouts: PackedParamLayout[]): THREE.UniformsGroup {
		const paramGroup = new THREE.UniformsGroup();
		paramGroup.setName("MaterialParams");
		for (const l of paramLayouts) {
			if (l.kind == "scalar") {
				paramGroup.add(new THREE.Uniform(l.defaultValue as number));
			} else if (l.kind == "color") {
				const c = l.defaultValue as [number, number, number];
				paramGroup.add(new THREE.Uniform(new THREE.Vector4(c[0] ?? 0, c[1] ?? 0, c[2] ?? 0, 0)));
			} else {
				// ramp → vec4[6]; one Uniform PER stop, nested-array entry (see TerrainMaterialManager.buildParamGroup).
				const stops = (l.defaultValue as number[]) ?? [];
				const quads: THREE.Vector4[] = [];
				for (let i = 0; i < stops.length; i += 4) {
					quads.push(new THREE.Vector4(stops[i] ?? 0, stops[i + 1] ?? 0, stops[i + 2] ?? 0, stops[i + 3] ?? 0));
				}
				const last = quads[quads.length - 1] ?? new THREE.Vector4(1, 0, 0, 0);
				while (quads.length < 6) quads.push(new THREE.Vector4(1, last.y, last.z, last.w));
				paramGroup.add(quads.map(q=>new THREE.Uniform(q)));
			}
		}
		return paramGroup;
	}

	/** Set the surface-material frame uniforms on a clone (per-frame values shared across meshes:
	 *  metres-per-pixel for the AA/detail scale, the light rig, the mesh's world Z origin for the
	 *  `geoHeight` primitive, and camera position for the real view vector). Call per frame or on
	 *  camera/light change. */
	setFrameUniforms(
		mat: THREE.ShaderMaterial,
		opts: {
			metersPerPixel?: number;
			lightDir?: [number, number, number];
			ambient?: number;
			strength?: number;
			surfaceElevation?: number;
			cameraPos?: [number, number, number];
		} = {},
	): void {
		const u = mat.uniforms;
		if (u.u_metersPerPixel) (u.u_metersPerPixel.value as number) = opts.metersPerPixel ?? 0.05;
		if (u.u_lightDir) (u.u_lightDir.value as THREE.Vector3).set(...(opts.lightDir ?? [0.7071, 0, 0.7071]));
		if (u.u_ambient) (u.u_ambient.value as number) = opts.ambient ?? 0.2;
		if (u.u_sunIntensity) (u.u_sunIntensity.value as number) = opts.strength ?? 1.0;
		if (u.u_surfaceElevation) (u.u_surfaceElevation.value as number) = opts.surfaceElevation ?? 0;
		// Buildings are always 3D meshes — the real view vector in specular (D10) is always correct.
		if (u.u_displaceScale) (u.u_displaceScale.value as number) = 1;
		if (u.u_cameraPos && opts.cameraPos) (u.u_cameraPos.value as THREE.Vector3).set(...opts.cameraPos);
	}

	/** Pack the recipe's param UBO floats for the given per-instance overrides (keyed by global
	 *  key `${recipeID}/${paramKey}` — empty for the built-in defaults). */
	packParams(overrides: {[globalKey: string]: ParamValue} = {}, forRecipeID = ""): {floats: Float32Array; globalKey: string} {
		if (!this.program) throw new Error("[SurfaceMaterialManager] getSurfaceMaterial must run before packParams");
		const entry = this.program;
		const fills: {floats: Float32Array; globalKey: string} = {
			floats: packMaterialParams(entry.paramLayouts, overrides),
			globalKey: `${forRecipeID}/${Object.keys(overrides)[0] ?? ""}`,
		};
		return fills;
	}

	/** Dispose the compiled program(s) + cache (called on renderer unmount). */
	dispose(): void {
		for (const entry of this.programs.values()) entry.material.dispose();
		this.programs.clear();
		this.program = null;
		this.retireWarmMaterials();
		this.warmRenderer = null;
	}
}

/** Strip the leading `#version 300 es` line from an assembled fragment (three prepends its own
 *  for a `glslVersion: GLSL3` ShaderMaterial). */
export function stripSurfaceVersionHeader(glsl: string): string {
	return glsl.startsWith("#version 300 es\n") ? glsl.slice("#version 300 es\n".length) : glsl;
}

/** Build the three.js `ShaderMaterial` template for a surface-mode assembled fragment.
 *  Uses the surface vertex shader (forwards UV + geometry normal); sets the full surface uniform
 *  set to neutral defaults. Shared by the live template; the renderer clones it per mesh. */
export function buildSurfaceMaterialTemplate(
	assembled: AssembleResult,
	_paramLayouts: PackedParamLayout[],
	paramGroup: () => THREE.UniformsGroup,
	THREE: typeof import("three"),
	transparent = false,
): THREE.ShaderMaterial {
	const material = new THREE.ShaderMaterial({
		glslVersion: THREE.GLSL3,
		vertexShader: SURFACE_VERTEX_GLSL,
		fragmentShader: stripSurfaceVersionHeader(assembled.glsl),
		uniforms: {
			u_metersPerPixel: {value: 0.05},
			u_displaceScale: {value: 1},
			u_baseAlbedo: {value: new THREE.Vector3(1, 1, 1)},
			u_origin: {value: new THREE.Vector2(0, 0)},
			u_chunkSize: {value: 1},
			u_geoHeight: {value: null},
			u_surfaceElevation: {value: 0},
			u_lightDir: {value: new THREE.Vector3(0.7071, 0, 0.7071)},
			u_ambient: {value: 0.2},
			u_sunIntensity: {value: 1.0},
			u_slopeScale: {value: 1.0},
			u_specStrength: {value: 0.15},
			u_opacity: {value: transparent ? 0.35 : 1.0},
			u_cameraPos: {value: new THREE.Vector3(0, 0, 1500)},
			u_shadowMap: {value: null},
			u_shadowMatrix: {value: new THREE.Matrix4()},
			u_shadowMapSize: {value: new THREE.Vector2(2048, 2048)},
			u_shadowBias: {value: -0.0015},
			u_shadowEnabled: {value: 0},
			u_varianceGain: {value: 1},
			// Ambient-detail uniforms — surface mode sets detailStrength 0 so the shader band is
			// an exact no-op (a building material rarely needs the pixel-scale terrain residual).
			u_ambCellSize: {value: 1},
			u_ambWavelength: {value: 1000},
			u_ambLacunarity: {value: 2.0},
			u_ambPersistence: {value: 0.5},
			u_ambAmplitude: {value: 0},
			u_ambDetailStrength: {value: 0},
			u_ambOctaves: {value: 0},
			u_ambDetailOctaves: {value: 0},
			u_ambFractalType: {value: 0},
			u_ambOffset: {value: new THREE.Vector2(0, 0)},
			u_ambSeed: {value: 0},
			u_ambientWeightMask: {value: null},
			u_ambientWeightMaskEnabled: {value: 0},
			u_geoNormal: {value: null},
		},
		transparent,
		depthWrite: !transparent,
	});
	material.uniformsGroups = [paramGroup()];
	return material;
}
